// Production-safe view helpers. Nothing here knows about the preview's fixtures or store.
//
// Rules the Queue/Fleet views follow (review finding P1, "Stored HTML can enter the dashboard"):
//  - data only ever reaches the page as a text node or a validated attribute, through h();
//  - a route id from data is always percent-encoded before it becomes part of a hash link;
//  - server text that might carry a credential is redacted before it is displayed.

export { h, mount, srOnly, uid, safeUrl, type Child } from "../../ui/dom";

/** `#/clients/<id>` with the id percent-encoded, so data cannot add path segments, queries or markup. */
export function clientHref(id: unknown): string {
  return `#/clients/${encodeURIComponent(String(id ?? ""))}`;
}

/** Text of a bounded length with control characters replaced, safe to show in a cell or notice. */
export function tidy(value: unknown, max = 120): string {
  const text = value === null || value === undefined ? "" : String(value);
  const clean = Array.from(text, (ch) => (ch.charCodeAt(0) < 32 && ch !== "\n" ? "?" : ch === "\n" ? " " : ch)).join("");
  return clean.length > max ? `${clean.slice(0, max)}…` : clean;
}

const SECRET_PATTERNS: [RegExp, string][] = [
  [/\bBearer\s+[A-Za-z0-9._~+/=-]{8,}/gi, "Bearer [redacted]"],
  [/\b(authorization|api[_-]?key|access[_-]?token|refresh[_-]?token|token|secret|password|passwd|client[_-]?secret)(["']?\s*[:=]\s*)("[^"]*"|'[^']*'|[^\s,;&]+)/gi, "$1$2[redacted]"],
  [/\b(?:sk|pk|rk|ghp|gho|ghs|xox[abprs]|AKIA)[A-Za-z0-9_-]{12,}\b/g, "[redacted]"],
  [/\b[A-Fa-f0-9]{32,}\b/g, "[redacted]"],
  [/\b[A-Za-z0-9_-]{40,}\b/g, "[redacted]"],
];

/** Masks things that look like credentials in server-provided text. Defensive: the server should not send them. */
export function redactSecrets(text: string): string {
  return SECRET_PATTERNS.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), text);
}

/** Server error text prepared for display: redacted, single line, bounded. */
export function safeMessage(value: unknown, max = 240): string {
  return tidy(redactSecrets(tidy(value, 2000)), max);
}

/** Epoch seconds (as the jobs table stores them) to a local string, or null when missing/invalid. */
export function formatEpoch(seconds: unknown): { text: string; iso: string } | null {
  const n = typeof seconds === "number" ? seconds : typeof seconds === "string" && seconds.trim() !== "" ? Number(seconds) : NaN;
  if (!Number.isFinite(n) || n <= 0) return null;
  const d = new Date(n * 1000);
  return Number.isNaN(d.getTime()) ? null : { text: d.toLocaleString(), iso: d.toISOString() };
}

/** A count from the API that may arrive as null, a string or garbage. */
export function countOf(value: unknown): number {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** Looks up a known key without ever reaching the prototype ("toString", "constructor", "__proto__"). */
export function ownValue<T>(map: Record<string, T>, key: unknown): T | undefined {
  return typeof key === "string" && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;
}
